import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { formatApiError } from "@/lib/api";
import { ArrowRight, Question } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-50">
      <div className="relative hidden md:block grain overflow-hidden">
        <div className="absolute inset-0 bg-[#0a0a0b]" />
        <img
          src="https://images.unsplash.com/photo-1493397212122-2b85dda8106b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMGxpbmVzfGVufDB8fHx8MTc4NjE4NDU1Mnww&ixlib=rb-4.1.0&q=85"
          alt="Architecture lines"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-20 rounded-md bg-white grid place-items-center overflow-hidden px-1">
              <img src="/soham-logo.jpg" alt="Soham Express logo" className="h-full w-full object-contain" data-testid="login-logo" />
            </div>
            <div>
              <div className="font-heading font-bold tracking-tight text-white">
                Sohom Express
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                Expense Suite
              </div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-400 mb-3">
              Office Expense Control
            </div>
            <h1 className="font-heading text-4xl leading-[1.05] font-semibold text-white">
              Every rupee, routed through the right hands.
            </h1>
          </div>
          <div />
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          className="w-full max-w-sm bg-white border border-zinc-200 rounded-lg p-8"
          data-testid="login-form"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Sign in
          </div>
          <h2 className="font-heading text-2xl font-semibold mt-1">
            Access the dashboard
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Use your office credentials to continue.
          </p>
          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-1"
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? "Signing in…" : (<>Continue <ArrowRight size={16} className="ml-1.5" /></>)}
            </Button>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-zinc-200" />
              <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">or</span>
              <div className="h-px flex-1 bg-zinc-200" />
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={googleLogin}
              data-testid="google-login-btn"
              className="w-full border-zinc-300 hover:bg-zinc-50"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" className="mr-2" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
              </svg>
              Sign in with Google
            </Button>
          </div>
          <div className="mt-6 text-sm">
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => setForgotOpen(true)}
              className="text-blue-700 font-medium hover:underline inline-flex items-center gap-1.5"
            >
              <Question size={15} weight="duotone" /> Forgot password?
            </button>
          </div>
        </form>

        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent data-testid="forgot-password-dialog" className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading">Forgot your password?</DialogTitle>
              <DialogDescription>
                Accounts on this platform are managed by the Admin.
              </DialogDescription>
            </DialogHeader>
            <div className="text-sm text-zinc-700 space-y-2">
              <p>
                Please contact your <span className="font-semibold">Admin</span> to reset
                your password. The Admin can set a new password for your account from the
                <span className="font-semibold"> Users &amp; Roles</span> page.
              </p>
              <p className="text-xs text-zinc-500">
                New user accounts are also created by the Admin only.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
